import type { SVGProps } from 'react';

/**
 * アイコンは 1 セットだけ（05 §3.7: Material Symbols Rounded 相当のアウトライン）。
 * 外部フォントを読まずに済むよう、24 グリッドの線画を自前で持つ。サイズは 20 / 24 / 28 の 3 種。
 * 色は currentColor（text/* / state/* / brand/* のトークンから取る。アイコン専用の色は作らない）。
 */
export type IconName =
  | 'inbox'
  | 'chat_bubble'
  | 'home_repair_service'
  | 'settings'
  | 'photo_camera'
  | 'search'
  | 'close'
  | 'arrow_back'
  | 'chevron_right'
  | 'chevron_left'
  | 'expand_more'
  | 'expand_less'
  | 'check'
  | 'check_circle'
  | 'radio_button_checked'
  | 'radio_button_unchecked'
  | 'circle'
  | 'undo'
  | 'warning'
  | 'error'
  | 'info'
  | 'visibility'
  | 'flag'
  | 'schedule'
  | 'refresh'
  | 'attach_file'
  | 'menu'
  | 'dark_mode'
  | 'light_mode'
  | 'add'
  | 'delete'
  | 'edit'
  | 'download'
  | 'upload'
  | 'logout'
  | 'notifications'
  | 'person'
  | 'group'
  | 'more_vert'
  | 'description'
  | 'receipt'
  | 'business'
  | 'account_tree'
  | 'shield'
  | 'key'
  | 'history'
  | 'list'
  | 'table_view'
  | 'yen'
  | 'inventory'
  | 'route'
  | 'swap_horiz'
  | 'assignment'
  | 'home'
  | 'open_in_new'
  | 'filter_list'
  | 'wifi'
  | 'image'
  | 'event'
  | 'send'
  | 'help'
  | 'payments'
  | 'bar_chart'
  | 'bolt'
  | 'lock'
  | 'mail'
  | 'drag_handle'
  | 'arrow_upward'
  | 'arrow_downward'
  | 'block'
  | 'folder'
  | 'star'
  | 'link'
  | 'content_copy'
  | 'mic'
  | 'call'
  | 'videocam';

const PATHS: Record<IconName, string> = {
  star: 'm12 3 2.8 5.7 6.2.9-4.5 4.4 1.1 6.2L12 17.3l-5.6 2.9 1.1-6.2L3 9.6l6.2-.9z',
  inbox: 'M5 4h14l2 9v7H3v-7zM3 13h5l2 3h4l2-3h5',
  chat_bubble: 'M4 5h16v11H9l-5 4z',
  home_repair_service: 'M3 10a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2v9a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1zM8 8V5h8v3M3 13h18M10 13v2M14 13v2',
  settings: 'M12 15a3 3 0 1 0 0-6 3 3 0 0 0 0 6zM12 2v3M12 19v3M2 12h3M19 12h3M4.9 4.9l2.1 2.1M17 17l2.1 2.1M4.9 19.1 7 17M17 7l2.1-2.1',
  photo_camera: 'M4 8h3l2-3h6l2 3h3v11H4zM12 16.5a3.5 3.5 0 1 0 0-7 3.5 3.5 0 0 0 0 7z',
  search: 'M11 17a6 6 0 1 0 0-12 6 6 0 0 0 0 12zM20 20l-4.5-4.5',
  close: 'M6 6l12 12M18 6 6 18',
  arrow_back: 'M20 12H5M11 5l-7 7 7 7',
  chevron_right: 'm9 6 6 6-6 6',
  chevron_left: 'm15 6-6 6 6 6',
  expand_more: 'm6 9 6 6 6-6',
  expand_less: 'm6 15 6-6 6 6',
  check: 'm5 12 5 5L20 7',
  check_circle: 'M12 21a9 9 0 1 0 0-18 9 9 0 0 0 0 18zM8 12l3 3 5-6',
  radio_button_checked: 'M12 21a9 9 0 1 0 0-18 9 9 0 0 0 0 18zM12 15.5a3.5 3.5 0 1 0 0-7 3.5 3.5 0 0 0 0 7z',
  radio_button_unchecked: 'M12 21a9 9 0 1 0 0-18 9 9 0 0 0 0 18z',
  circle: 'M12 17a5 5 0 1 0 0-10 5 5 0 0 0 0 10z',
  undo: 'M9 14 4 9l5-5M4 9h10a6 6 0 0 1 0 12h-3',
  warning: 'M12 3 2.5 20h19zM12 9v5M12 17.5v.5',
  error: 'M12 21a9 9 0 1 0 0-18 9 9 0 0 0 0 18zM12 8v5M12 16v.5',
  info: 'M12 21a9 9 0 1 0 0-18 9 9 0 0 0 0 18zM12 11v5M12 7.5v.5',
  visibility: 'M2 12s3.5-6 10-6 10 6 10 6-3.5 6-10 6S2 12 2 12zM12 15a3 3 0 1 0 0-6 3 3 0 0 0 0 6z',
  flag: 'M5 21V4h13l-2 5 2 5H5',
  schedule: 'M12 21a9 9 0 1 0 0-18 9 9 0 0 0 0 18zM12 7v5l3 2',
  refresh: 'M20 12a8 8 0 1 1-2.3-5.7M20 4v5h-5',
  attach_file: 'm16 6-7.5 7.5a3 3 0 0 0 4.2 4.2L20 10.5a5 5 0 0 0-7.1-7.1L5.5 10.8a7 7 0 0 0 9.9 9.9L19 17',
  menu: 'M4 6h16M4 12h16M4 18h16',
  dark_mode: 'M20 14.5A8 8 0 0 1 9.5 4a8 8 0 1 0 10.5 10.5z',
  light_mode: 'M12 16a4 4 0 1 0 0-8 4 4 0 0 0 0 8zM12 2v2M12 20v2M2 12h2M20 12h2M4.9 4.9l1.4 1.4M17.7 17.7l1.4 1.4M4.9 19.1l1.4-1.4M17.7 6.3l1.4-1.4',
  add: 'M12 5v14M5 12h14',
  delete: 'M4 7h16M9 7V4h6v3M6 7l1 13h10l1-13M10 11v6M14 11v6',
  edit: 'm4 20 4-1L19 8l-3-3L5 16zM14 7l3 3',
  download: 'M12 4v11M7 10l5 5 5-5M4 20h16',
  upload: 'M12 15V4M7 9l5-5 5 5M4 20h16',
  logout: 'M10 4H5v16h5M14 8l4 4-4 4M18 12H9',
  notifications: 'M6 17v-6a6 6 0 0 1 12 0v6l2 2H4zM10 21h4',
  person: 'M12 12a4 4 0 1 0 0-8 4 4 0 0 0 0 8zM4 21a8 8 0 0 1 16 0',
  group: 'M9 11.5a3.5 3.5 0 1 0 0-7 3.5 3.5 0 0 0 0 7zM2 20a7 7 0 0 1 14 0M16 4.5a3.5 3.5 0 0 1 0 7M22 20a7 7 0 0 0-5-6.7',
  more_vert: 'M12 5.5v.5M12 12v.5M12 18.5v.5',
  description: 'M6 3h8l4 4v14H6zM14 3v4h4M9 12h6M9 16h6',
  receipt: 'M5 3h14v18l-2.3-1.5L14.3 21 12 19.5 9.7 21 7.3 19.5 5 21zM8 8h8M8 12h8M8 16h5',
  business: 'M4 21V5l8-3v19M12 9l8 3v9M4 21h16M7 8h2M7 12h2M7 16h2M15 14h2M15 18h2',
  account_tree: 'M6 8v11h9M6 12h9M3 3h6v5H3zM15 9.5h6v5h-6zM15 16.5h6v4.5h-6z',
  shield: 'M12 3 4 6v6c0 5 3.5 8 8 9 4.5-1 8-4 8-9V6z',
  key: 'M8 16a4 4 0 1 0 0-8 4 4 0 0 0 0 8zM12 12h9M18 12v3M15 12v2',
  history: 'M3 12a9 9 0 1 0 3-6.7M3 4v5h5M12 7v5l3 2',
  list: 'M8 6h13M8 12h13M8 18h13M3.5 6h.5M3.5 12h.5M3.5 18h.5',
  table_view: 'M3 6a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2v12a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2zM3 10h18M9 10v10M15 10v10',
  yen: 'M7 4l5 8 5-8M12 12v8M8 15h8M8 18h8',
  inventory: 'M3 7h18v13H3zM3 7l2-4h14l2 4M10 11h4',
  route: 'M6 7.5a2.5 2.5 0 1 0 0-5 2.5 2.5 0 0 0 0 5zM18 21.5a2.5 2.5 0 1 0 0-5 2.5 2.5 0 0 0 0 5zM6 7.5V12a4 4 0 0 0 4 4h6',
  swap_horiz: 'M7 10h13l-3-3M17 14H4l3 3',
  assignment: 'M4 6a2 2 0 0 1 2-2h12a2 2 0 0 1 2 2v13a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2zM9 3h6v3H9zM8 11h8M8 15h5',
  home: 'M3 11 12 3l9 8v10h-6v-6H9v6H3z',
  open_in_new: 'M14 4h6v6M20 4l-9 9M19 14v6H4V5h6',
  filter_list: 'M4 6h16M7 12h10M10 18h4',
  wifi: 'M2 9a15 15 0 0 1 20 0M5 12.5a10 10 0 0 1 14 0M8.5 16a5 5 0 0 1 7 0M12 19.5v.5',
  image: 'M3 6a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2v12a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2zM8.5 10.5a1.5 1.5 0 1 0 0-3 1.5 1.5 0 0 0 0 3zM21 16l-5-5-9 9',
  event: 'M3 7a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2v12a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2zM3 10h18M8 3v4M16 3v4',
  send: 'm3 3 18 9-18 9 4-9z',
  help: 'M12 21a9 9 0 1 0 0-18 9 9 0 0 0 0 18zM9.5 9.5a2.5 2.5 0 1 1 3.5 2.3c-.7.4-1 1-1 1.7M12 17v.5',
  payments: 'M2 8a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v8a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2zM12 14.5a2.5 2.5 0 1 0 0-5 2.5 2.5 0 0 0 0 5zM6 12h.01M18 12h.01',
  bar_chart: 'M4 20v-8M10 20V4M16 20v-6M2 20h20',
  bolt: 'M13 2 4 14h7l-1 8 9-12h-7z',
  lock: 'M5 12a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2v7a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2zM8 10V7a4 4 0 0 1 8 0v3',
  mail: 'M3 7a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2v10a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2zM3 7l9 6 9-6',
  drag_handle: 'M4 9h16M4 15h16',
  arrow_upward: 'M12 20V5M5 12l7-7 7 7',
  arrow_downward: 'M12 4v15M5 12l7 7 7-7',
  block: 'M12 21a9 9 0 1 0 0-18 9 9 0 0 0 0 18zM5.6 5.6l12.8 12.8',
  // storage（ファイル共有）
  folder: 'M3 6a2 2 0 0 1 2-2h5l2 3h7a2 2 0 0 1 2 2v9a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z',
  link: 'M10 14a4 4 0 0 0 5.7 0l3-3a4 4 0 0 0-5.7-5.7l-1 1M14 10a4 4 0 0 0-5.7 0l-3 3a4 4 0 0 0 5.7 5.7l1-1',
  content_copy: 'M9 9h11v12H9zM5 15H4V3h11v2',
  mic: 'M12 15a3 3 0 0 0 3-3V6a3 3 0 0 0-6 0v6a3 3 0 0 0 3 3zM6 11a6 6 0 0 0 12 0M12 17v4M9 21h6',
  call: 'M5 4h4l2 5-2.5 2.5a16 16 0 0 0 4 4L15 13l5 2v4c0 .6-.4 1-1 1C10.7 20 4 13.3 4 5c0-.6.4-1 1-1z',
  videocam: 'M3 6h13v12H3zM16 10l5-3v10l-5-3',
};

export interface IconProps extends Omit<SVGProps<SVGSVGElement>, 'name'> {
  name: IconName;
  /** 20 / 24 / 28 の 3 種のみ（05 §3.7） */
  size?: 20 | 24 | 28;
  /** 読み上げが要るときだけ渡す（ボタンに文字があるなら渡さない） */
  label?: string;
}

export function Icon({ name, size = 24, label, className, style, ...rest }: IconProps) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={2}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden={label ? undefined : true}
      role={label ? 'img' : undefined}
      aria-label={label}
      focusable="false"
      className={className}
      style={{
        width: `calc(${size}px * var(--ui-icon-scale, 1))`,
        height: `calc(${size}px * var(--ui-icon-scale, 1))`,
        ...style,
      }}
      {...rest}
    >
      <path d={PATHS[name]} />
    </svg>
  );
}

export const ICON_NAMES = Object.keys(PATHS) as IconName[];
